import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './common/LanguageSwitcher';
import './SchoolPage.css';

const HighSchoolStreamPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const { stream } = useParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Modules by stream — subject names from i18n (resources.subjects.*)
  const streamModuleKeys = {
    'sciences': [
      { id: 'mathematics', subjectKey: 'mathematics', icon: 'calculate', color: '#34a853' },
      { id: 'natural-sciences', subjectKey: 'naturalSciences', icon: 'biotech', color: '#4caf50' },
      { id: 'physical-sciences', subjectKey: 'physicalSciences', icon: 'science', color: '#fbbc04' },
      { id: 'english', subjectKey: 'english', icon: 'language', color: '#673ab7' },
      { id: 'french', subjectKey: 'french', icon: 'translate', color: '#ea4335' },
      { id: 'arabic', subjectKey: 'arabic', icon: 'menu_book', color: '#1a73e8' },
      { id: 'philosophy', subjectKey: 'philosophy', icon: 'psychology', color: '#9c27b0' },
      { id: 'islamic-education', subjectKey: 'islamicEducation', icon: 'mosque', color: '#9c27b0' }
    ],
    'math': [
      { id: 'mathematics', subjectKey: 'mathematics', icon: 'calculate', color: '#34a853' },
      { id: 'physical-sciences', subjectKey: 'physicalSciences', icon: 'science', color: '#fbbc04' },
      { id: 'natural-sciences', subjectKey: 'naturalSciences', icon: 'biotech', color: '#4caf50' },
      { id: 'english', subjectKey: 'english', icon: 'language', color: '#673ab7' },
      { id: 'french', subjectKey: 'french', icon: 'translate', color: '#ea4335' },
      { id: 'arabic', subjectKey: 'arabic', icon: 'menu_book', color: '#1a73e8' },
      { id: 'philosophy', subjectKey: 'philosophy', icon: 'psychology', color: '#9c27b0' },
      { id: 'islamic-education', subjectKey: 'islamicEducation', icon: 'mosque', color: '#9c27b0' }
    ],
    'literature': [
      { id: 'arabic', subjectKey: 'arabic', icon: 'menu_book', color: '#1a73e8' },
      { id: 'english', subjectKey: 'english', icon: 'language', color: '#673ab7' },
      { id: 'french', subjectKey: 'french', icon: 'translate', color: '#ea4335' },
      { id: 'history-geography', subjectKey: 'historyGeography', icon: 'public', color: '#ff9800' },
      { id: 'philosophy', subjectKey: 'philosophy', icon: 'psychology', color: '#9c27b0' },
      { id: 'islamic-education', subjectKey: 'islamicEducation', icon: 'mosque', color: '#9c27b0' },
      { id: 'mathematics', subjectKey: 'mathematics', icon: 'calculate', color: '#34a853' }
    ],
    'technical-math': [
      { id: 'mathematics', subjectKey: 'mathematics', icon: 'calculate', color: '#34a853' },
      { id: 'physical-sciences', subjectKey: 'physicalSciences', icon: 'science', color: '#fbbc04' },
      { id: 'mechanical-technology', subjectKey: 'mechanicalTechnology', icon: 'engineering', color: '#607d8b' },
      { id: 'electrical-technology', subjectKey: 'electricalTechnology', icon: 'electrical_services', color: '#ff5722' },
      { id: 'english', subjectKey: 'english', icon: 'language', color: '#673ab7' },
      { id: 'french', subjectKey: 'french', icon: 'translate', color: '#ea4335' },
      { id: 'arabic', subjectKey: 'arabic', icon: 'menu_book', color: '#1a73e8' },
      { id: 'philosophy', subjectKey: 'philosophy', icon: 'psychology', color: '#9c27b0' },
      { id: 'islamic-education', subjectKey: 'islamicEducation', icon: 'mosque', color: '#9c27b0' }
    ],
    'management-economics': [
      { id: 'management', subjectKey: 'management', icon: 'business', color: '#1a73e8' },
      { id: 'economics', subjectKey: 'economics', icon: 'trending_up', color: '#34a853' },
      { id: 'mathematics', subjectKey: 'mathematics', icon: 'calculate', color: '#34a853' },
      { id: 'accounting', subjectKey: 'accounting', icon: 'account_balance_wallet', color: '#fbbc04' },
      { id: 'law', subjectKey: 'law', icon: 'gavel', color: '#ea4335' },
      { id: 'english', subjectKey: 'english', icon: 'language', color: '#673ab7' },
      { id: 'arabic', subjectKey: 'arabic', icon: 'menu_book', color: '#1a73e8' },
      { id: 'philosophy', subjectKey: 'philosophy', icon: 'psychology', color: '#9c27b0' },
      { id: 'islamic-education', subjectKey: 'islamicEducation', icon: 'mosque', color: '#9c27b0' }
    ]
  };

  const streamNameKeys = {
    'sciences': 'resources.streams.sciences',
    'math': 'resources.streams.math',
    'literature': 'resources.streams.literature',
    'technical-math': 'resources.streams.technicalMath',
    'management-economics': 'resources.streams.managementEconomics'
  };

  const modules = streamModuleKeys[stream] || [];
  const streamNameTKey = streamNameKeys[stream];
  const streamName = streamNameTKey ? t(streamNameTKey) : stream;

  const handleModuleClick = (moduleId) => {
    navigate(`/resources/high/${stream}/${moduleId}`);
  };

  return (
    <div className="school-page">
      {/* Navigation Bar */}
      <nav className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="material-icons">school</span>
            <span>Nativelike</span>
          </div>
          <div className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); setIsMobileMenuOpen(false); }}>{t('nav.features')}</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); setIsMobileMenuOpen(false); }}>{t('nav.howItWorks')}</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); setIsMobileMenuOpen(false); }}>{t('nav.pricing')}</a>
            <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToSection('testimonials'); setIsMobileMenuOpen(false); }}>{t('nav.reviews')}</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); setIsMobileMenuOpen(false); }} className="active">{t('nav.resources')}</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => scrollToSection('login')}>{t('nav.login')}</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>{t('nav.startLearning')}</button>
            <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              <span className="material-icons">{isMobileMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="school-container" style={{ paddingTop: '80px' }}>
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); }}>{t('nav.resources')}</a>
          <span className="material-icons">chevron_right</span>
          <a href="/resources/high" onClick={(e) => { e.preventDefault(); navigate('/resources/high'); }}>{t('resources.highSchool')}</a>
          <span className="material-icons">chevron_right</span>
          <span>{streamName}</span>
        </div>

        {/* Header */}
        <div className="school-header">
          <h1 className="school-title">
            <span className="material-icons">menu_book</span>
            {streamName} {t('resources.stream')}
          </h1>
          <p className="school-subtitle">
            {t('resources.selectSubjectHigh')}
          </p>
        </div>

        {/* Modules Grid */}
        <div className="modules-grid">
          {modules.map((module) => (
            <div
              key={module.id}
              className="module-card"
              onClick={() => handleModuleClick(module.id)}
            >
              <div className="module-icon" style={{ backgroundColor: `${module.color}15`, color: module.color }}>
                <span className="material-icons">{module.icon}</span>
              </div>
              <h3>{t('resources.subjects.' + module.subjectKey)}</h3>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HighSchoolStreamPage;
